import React, { createContext, useCallback, useContext, useMemo } from "react";

import { normalizeThemeMode } from "../theme/themePreference";

const defaultThemeController = Object.freeze({
  mode: "light",
  setMode: () => {},
  toggleMode: () => {},
});

const ProductThemeModeContext = createContext(defaultThemeController);

/**
 * Gives module-level display controls access to the one App-owned theme.
 * Consumers request a change through setMode; they never write storage or DOM
 * attributes themselves.
 */
export function useProductThemeMode() {
  return useContext(ProductThemeModeContext);
}

/**
 * Keeps the authenticated shell on one lightweight product theme contract.
 *
 * The legacy MUI-only pages are code-split by route, so mounting a global MUI
 * ThemeProvider here would force Emotion and the complete theme engine into
 * every sign-in and shell navigation. Product UI uses CSS tokens instead;
 * legacy routes retain MUI's safe default theme after their own chunk loads.
 */
export function ProductThemeProvider({ mode = "light", onModeChange, children }) {
  const normalizedMode = normalizeThemeMode(mode);

  const setMode = useCallback((nextValue) => {
    const requestedMode = typeof nextValue === "function"
      ? nextValue(normalizedMode)
      : nextValue;
    const nextMode = normalizeThemeMode(requestedMode, normalizedMode);

    if (nextMode !== normalizedMode) {
      onModeChange?.(nextMode);
    }
  }, [normalizedMode, onModeChange]);

  const controller = useMemo(() => ({
    mode: normalizedMode,
    setMode,
    toggleMode: () => setMode((current) => (current === "dark" ? "light" : "dark")),
  }), [normalizedMode, setMode]);

  return (
    <ProductThemeModeContext.Provider value={controller}>
      {children}
    </ProductThemeModeContext.Provider>
  );
}
